/**
 * Time-series drill-down ⇄ the canonical date range.
 *
 * Clicking a Collections Over Time period narrows the report's own date range
 * to that period (every projection follows — Model A). The previous range and
 * preset are kept in the controller's drill history so "Back" (or Escape)
 * returns one level. Pure and React-free.
 */
import { REPORT_DATE_PRESET_LABELS, type ReportDateRange } from '@helios/domain';
import type { ReportDateSelection, ReportUiState } from './report-state-logic';

function localDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) => d.toLocaleDateString('en-PH', opts);

/**
 * Human label for a date range (display only):
 * one day "Sep 24, 2026"; a whole calendar month "Sep 2026";
 * otherwise "Sep 21 – Oct 4, 2026" (years shown on both ends when they differ).
 */
export function formatDateRangeLabel(range: ReportDateRange): string {
  const from = localDate(range.from);
  const to = localDate(range.to);
  if (range.from === range.to)
    return fmt(from, { month: 'short', day: 'numeric', year: 'numeric' });
  const lastOfMonth = new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate();
  if (
    from.getDate() === 1 &&
    to.getDate() === lastOfMonth &&
    from.getFullYear() === to.getFullYear() &&
    from.getMonth() === to.getMonth()
  ) {
    return fmt(from, { month: 'short', year: 'numeric' });
  }
  if (from.getFullYear() !== to.getFullYear()) {
    return `${fmt(from, { month: 'short', day: 'numeric', year: 'numeric' })} – ${fmt(to, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }
  return `${fmt(from, { month: 'short', day: 'numeric' })} – ${fmt(to, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
}

function selectionLabel(selection: ReportDateSelection, range: ReportDateRange): string {
  return selection.kind === 'preset'
    ? REPORT_DATE_PRESET_LABELS[selection.id]
    : formatDateRangeLabel(range);
}

export interface TimeDrillFeedback {
  /** The period currently shown, e.g. "Sep 2026". */
  currentLabel: string;
  /** Where Back returns to, e.g. "YTD" or "Sep 1 – 26, 2026". */
  backLabel: string;
}

/** Feedback for the drill-down, or null when not drilled in. */
export function timeDrillFeedback<C extends string>(
  state: Pick<ReportUiState<C>, 'query' | 'dateDrillStack'>,
): TimeDrillFeedback | null {
  const previous = state.dateDrillStack[state.dateDrillStack.length - 1];
  if (!previous) return null;
  return {
    currentLabel: formatDateRangeLabel(state.query.dateRange),
    backLabel: selectionLabel(previous.dateSelection, previous.dateRange),
  };
}

export interface TimeDrillController<C extends string> {
  state: Pick<ReportUiState<C>, 'query' | 'dateDrillStack'>;
  drillDateRange: (range: ReportDateRange) => void;
  drillBack: () => void;
}

/** Wire a time-series visualization to the canonical date range. */
export function timeDrillBinding<C extends string>(
  controller: TimeDrillController<C>,
): {
  onPointActivate: (point: { from: string; to: string }) => void;
  drill: (TimeDrillFeedback & { onBack: () => void }) | null;
} {
  const feedback = timeDrillFeedback(controller.state);
  return {
    // A point's from/to are already clipped to the applied range.
    onPointActivate: (point) => controller.drillDateRange({ from: point.from, to: point.to }),
    drill: feedback ? { ...feedback, onBack: controller.drillBack } : null,
  };
}
